import { Phone, Mail, Clock, MapPin } from "lucide-react";
import SEO, { localBusinessSchema, breadcrumbSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs } from "../components/Shared";
import { LeadForm } from "../components/LeadForm";
import { BRAND, IMAGES } from "../data/siteData";

const crumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }];

const HOURS = [
  ["Monday – Friday", "7:00 AM – 6:00 PM"],
  ["Saturday", "8:00 AM – 4:00 PM"],
  ["Sunday", "Closed (installs by appointment)"],
];

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Vantyx Coatings | Free Garage Floor Estimates Phoenix"
        description="Contact Vantyx Coatings for a free garage floor coating estimate anywhere in the Phoenix Metro. Call 480-351-6577 or send the form — we respond within one business day."
        path="/contact"
        ogImage={IMAGES.garage1}
        schemas={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />

      <section className="hero-gradient py-20 md:py-24 px-6 md:px-12 grain relative" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>Talk to a Human</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
            Let's Talk About <span className="text-[#028090]">Your Garage</span>
          </h1>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16">
          <div className="bg-[#000000] p-8 md:p-12 teal-glow">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Request Your Free Estimate</h2>
            <p className="text-gray-400 text-sm mb-8">We respond within one business day — usually much faster.</p>
            <LeadForm dark sourceTag="contact-page" />
          </div>
          <div className="space-y-10">
            <div>
              <h3 className="font-display text-xl font-bold text-[#000000] mb-5">Get in touch</h3>
              <div className="space-y-4">
                <a href={BRAND.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 text-[#000000] font-semibold hover:text-[#028090] transition-colors duration-200">
                  <span className="w-11 h-11 bg-[#028090]/10 flex items-center justify-center"><Phone size={19} className="text-[#028090]" strokeWidth={1.5} /></span>
                  {BRAND.phone}
                </a>
                <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-[#000000] font-semibold hover:text-[#028090] transition-colors duration-200">
                  <span className="w-11 h-11 bg-[#028090]/10 flex items-center justify-center"><Mail size={19} className="text-[#028090]" strokeWidth={1.5} /></span>
                  {BRAND.email}
                </a>
                <p className="flex items-center gap-3 text-gray-600">
                  <span className="w-11 h-11 bg-[#028090]/10 flex items-center justify-center"><MapPin size={19} className="text-[#028090]" strokeWidth={1.5} /></span>
                  {BRAND.area}
                </p>
              </div>
            </div>
            <div>
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-[#000000] mb-5"><Clock size={19} className="text-[#028090]" strokeWidth={1.5} /> Business Hours</h3>
              <div className="border border-gray-200 divide-y divide-gray-200" data-testid="business-hours">
                {HOURS.map(([d, h]) => (
                  <div key={d} className="flex justify-between p-4 text-sm">
                    <span className="text-gray-600 font-medium">{d}</span>
                    <span className="text-[#000000] font-semibold">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-[#000000] mb-5">Service Area</h3>
              <iframe
                title="Vantyx Coatings service area — Phoenix Metro"
                src="https://www.google.com/maps?q=Phoenix%20Metropolitan%20Area%2C%20AZ&output=embed&z=9"
                className="w-full h-64 border border-gray-200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
